/** PMC / periodic chart settings — mirrors mobile chart setting shape. */

export interface PmcSectionSettings {
  show_pmc?: boolean;
  fitness_CTL_constant?: number;
  fitness_CTL_start?: number;
  fatigue_ATL_constant?: number;
  fatigue_ATL_start?: number;
  show_IF?: boolean;
  show_TSS?: boolean;
  show_CTL?: boolean;
  show_ATL?: boolean;
  show_form_TSB?: boolean;
  show_form_TSB_fill?: boolean;
}

export interface PeakSectionSettings {
  show_best_peaks?: boolean;
  number_of_best_peaks?: number;
  peak_types?: string;
  durations?: string[];
}

export interface WorkoutSummarySettings {
  show_workout_summery?: boolean;
  workout_summery_types?: string;
  group_by?: 'day' | 'week' | 'month' | string;
  show_planed_vs_actual_values?: boolean;
}

export interface PmcChartSettings {
  chart_title?: string;
  pmc?: PmcSectionSettings;
  best_peaks_1?: PeakSectionSettings;
  best_peaks_2?: PeakSectionSettings;
  workout_summery?: WorkoutSummarySettings;
  [key: string]: unknown;
}

/** Peak type prefixes — matches App\Models\Enums\PeakType. */
export const PEAK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'power', label: 'Power' },
  { value: 'heart_rate', label: 'Heart Rate' },
  { value: 'speed', label: 'Speed' },
  { value: 'pace', label: 'Pace' },
  { value: 'cadence', label: 'Cadence' },
  { value: 'pace_by_distance_peak', label: 'Pace by Distance' },
];

/** Peak duration suffixes — matches App\Models\Enums\PeakDurations. */
export const PEAK_DURATION_OPTIONS: { value: string; label: string }[] = [
  { value: '5sec', label: '5 sec' },
  { value: '10sec', label: '10 sec' },
  { value: '12sec', label: '12 sec' },
  { value: '20sec', label: '20 sec' },
  { value: '30sec', label: '30 sec' },
  { value: '1min', label: '1 min' },
  { value: '2min', label: '2 min' },
  { value: '5min', label: '5 min' },
  { value: '6min', label: '6 min' },
  { value: '10min', label: '10 min' },
  { value: '12min', label: '12 min' },
  { value: '20min', label: '20 min' },
  { value: '30min', label: '30 min' },
  { value: '60min', label: '60 min' },
  { value: '90min', label: '90 min' },
  { value: '120min', label: '120 min' },
  { value: '180min', label: '180 min' },
  { value: '5hrs', label: '5 hrs' },
  { value: '10hrs', label: '10 hrs' },
  { value: '24hrs', label: '24 hrs' },
];

/** Workout summary metric keys — matches App\Models\Enums\WorkouSummeryTypes. */
export const WORKOUT_SUMMARY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'tss_actual', label: 'TSS' },
  { value: 'distance', label: 'Distance' },
  { value: 'total_time', label: 'Duration' },
  { value: 'calories', label: 'Calories' },
  { value: 'if_actual', label: 'Intensity Factor' },
  { value: 'total_ascent', label: 'Elevation Gain' },
  { value: 'total_descent', label: 'Elevation Loss' },
  { value: 'work', label: 'Kilojoules' },
];

export const WORKOUT_GROUP_BY_OPTIONS: { value: string; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

export const BEST_PEAK_COUNT_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

export const DEFAULT_PMC_CHART_SETTINGS: PmcChartSettings = {
  pmc: {
    show_pmc: true,
    fitness_CTL_constant: 42,
    fitness_CTL_start: 0,
    fatigue_ATL_constant: 7,
    fatigue_ATL_start: 0,
    show_IF: false,
    show_TSS: false,
    show_CTL: true,
    show_ATL: true,
    show_form_TSB: true,
    show_form_TSB_fill: false,
  },
  workout_summery: {
    show_workout_summery: false,
    workout_summery_types: 'tss_actual',
    group_by: 'week',
    show_planed_vs_actual_values: false,
  },
  best_peaks_1: {
    show_best_peaks: false,
    number_of_best_peaks: 1,
    peak_types: 'power',
    durations: ['5sec', '10min'],
  },
  best_peaks_2: {
    show_best_peaks: false,
    number_of_best_peaks: 1,
    peak_types: 'heart_rate',
    durations: ['5sec', '10min'],
  },
};

/** Deep-merge stored user_chart.setting with PMC defaults. */
export function mergePmcSettings(raw: Record<string, unknown> | undefined): PmcChartSettings {
  const base = JSON.parse(JSON.stringify(DEFAULT_PMC_CHART_SETTINGS)) as PmcChartSettings;
  if (!raw) return base;
  return {
    ...base,
    ...raw,
    pmc: { ...base.pmc, ...(raw.pmc as PmcSectionSettings | undefined) },
    workout_summery: { ...base.workout_summery, ...(raw.workout_summery as WorkoutSummarySettings | undefined) },
    best_peaks_1: { ...base.best_peaks_1, ...(raw.best_peaks_1 as PeakSectionSettings | undefined) },
    best_peaks_2: { ...base.best_peaks_2, ...(raw.best_peaks_2 as PeakSectionSettings | undefined) },
  };
}

/** Convert bundled pmc_report API payload → periodic chart data shape. */
export function pmcReportToPeriodicData(raw: unknown, days: number): Record<string, unknown> {
  const r = raw as Record<string, unknown> | null | undefined;
  const key =
    days <= 14 ? 'last_7_days' :
    days <= 30 ? 'last_28_days' :
    days <= 90 ? 'last_90_days' :
    days <= 180 ? 'last_180_days' :
    days <= 365 ? 'last_365_days' : 'last_730_days';
  const bucket = r?.[key] as Record<string, unknown> | undefined;
  let pmc: Record<string, unknown>[] = [];

  const overlay = Array.isArray(r?.pmc) ? r.pmc as Record<string, unknown>[] : [];
  // The selected history bucket is authoritative; overlays may cover a shorter saved range.
  if (bucket?.default && typeof bucket.default === 'object' && !Array.isArray(bucket.default)) {
    const byDate = new Map(overlay.map(row => [String(row.calculation_date ?? row.date).slice(0, 10), row]));
    pmc = Object.entries(bucket.default as Record<string, Record<string, unknown>>).map(([date, row]) => {
      const calculationDate = row?.date ?? date;
      const extra = byDate.get(String(calculationDate).slice(0, 10));
      return {
        calculation_date: calculationDate,
        ctl: row?.ctl,
        atl: row?.atl,
        tsb: row?.tsb,
        tss_actual: row?.tss ?? row?.tss_actual ?? extra?.tss_actual ?? extra?.tss,
        if_actual: row?.if_actual ?? extra?.if_actual,
      };
    });
  } else {
    pmc = overlay.map(row => ({
      calculation_date: row?.calculation_date ?? row?.date,
      ctl: row?.ctl, atl: row?.atl, tsb: row?.tsb,
      tss_actual: row?.tss_actual ?? row?.tss,
      if_actual: row?.if_actual,
    }));
  }

  pmc.sort((a, b) => String(a.calculation_date).localeCompare(String(b.calculation_date)));

  return {
    pmc,
    training_metrics: r?.training_metrics,
    best_peaks_1: r?.best_peaks_1,
    best_peaks_2: r?.best_peaks_2,
    workout_summery: r?.workout_summery,
    metrics: r?.metrics,
    nutrition: r?.nutrition,
  };
}

/** Fallback merge from a periodic_chart sibling when PMC payload lacks overlay keys. */
export function mergePeakData(
  pmcData: Record<string, unknown>,
  periodicRaw: unknown,
  settings: PmcChartSettings,
): Record<string, unknown> {
  const pr = periodicRaw as Record<string, unknown> | null | undefined;
  if (!pr) return pmcData;
  const out = { ...pmcData };
  if (settings.best_peaks_1?.show_best_peaks && !out.best_peaks_1 && pr.best_peaks_1) {
    out.best_peaks_1 = pr.best_peaks_1;
  }
  if (settings.best_peaks_2?.show_best_peaks && !out.best_peaks_2 && pr.best_peaks_2) {
    out.best_peaks_2 = pr.best_peaks_2;
  }
  const ws = settings.workout_summery;
  const wantsSummary = ws?.show_workout_summery !== false;
  const wantsTss = settings.pmc?.show_TSS !== false;
  if ((wantsSummary || wantsTss) && !out.workout_summery && pr.workout_summery) {
    out.workout_summery = pr.workout_summery;
  }
  return out;
}
